/**
 * Inline Hedge Calculator
 * Handles hedge plant calculations on product pages
 * Updates quantity input based on hedge length and row density
 */

class HedgeCalculatorInline {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.variantId = container.dataset.variantId;
    this.plantsPerMeterSingle = parseFloat(container.dataset.plantsPerMeterSingle) || 0;
    this.plantsPerMeterDouble = parseFloat(container.dataset.plantsPerMeterDouble) || 0;

    // DOM elements
    this.rowButtons = container.querySelectorAll('.js-hedge-calculator-inline-row-button');
    this.rowTypeInput = container.querySelector('.js-hedge-calculator-inline-row-type');
    this.metresInput = container.querySelector('.js-hedge-calculator-inline-metres');
    this.plantsPerMeterText = container.querySelector('.hedge-calculator-inline__no-plants-text');
    this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);

    // State
    this.currentRowType = this.rowTypeInput?.value || 'double';
    // Prevent infinite loops
    this.isUpdating = false;
    this.variantChangeUnsubscriber = null;
    this.quantityUpdateUnsubscriber = null;

    this.translations = (window.translations && window.translations.hedgesCalculator) || {};

    // Initialize
    this.init();
  }

  init() {
    if (!this.validateMetafields()) {
      // Missing required metafields, calculator disabled
      return;
    }

    this.setupRowTypeButtons();

    // Override metres input validation BEFORE setting up the input
    // This ensures our override is in place before quantity-input initializes
    if (this.metresInput) {
      this.overrideMetresInputValidation();
    }

    this.setupMetresInput();

    // Wait a bit for quantity-selector to render
    setTimeout(() => {
      this.setupQuantityInput();
    }, 50);

    this.subscribeToVariantChanges();
    this.updateRowTypeVisibility();
    this.updatePlantsPerMeterText();
  }

  validateMetafields() {
    // Only need plants per metre values - calculation is simply: plantsPerMeter * metres
    if (!this.plantsPerMeterSingle || !this.plantsPerMeterDouble) {
      return false;
    }
    return true;
  }

  setupRowTypeButtons() {
    if (!this.rowButtons.length) return;

    // Set initial active state
    this.updateRowTypeButtonState();

    // Add click handlers
    this.rowButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const rowType = button.dataset.rowType;
        if (rowType && rowType !== this.currentRowType) {
          this.setRowType(rowType);
        }
      });
    });
  }

  setRowType(rowType) {
    if (rowType !== 'single' && rowType !== 'double') return;

    this.currentRowType = rowType;
    if (this.rowTypeInput) {
      this.rowTypeInput.value = rowType;
    }

    this.updateRowTypeButtonState();
    this.updatePlantsPerMeterText();

    // Preserve metres input value - only recalculate if user has entered a value > 0
    const metres = parseFloat(this.metresInput?.value) || 0;
    if (metres > 0) {
      // User has entered a value, recalculate with new row type
      this.calculateAndUpdateQuantity();
    } else {
      // Keep at 0, just reset quantity to minimum
      this.resetQuantityToMinimum();
    }
  }

  updateRowTypeButtonState() {
    this.rowButtons.forEach((button) => {
      const isActive = button.dataset.rowType === this.currentRowType;
      button.classList.toggle('hedge-calculator-inline__row-button--active', isActive);
      button.setAttribute('aria-pressed', isActive);
    });
  }

  updatePlantsPerMeterText() {
    if (!this.plantsPerMeterText) return;

    // Update data attributes from container (which gets updated on variant change)
    this.plantsPerMeterText.dataset.plantsPerMeterSingle = this.plantsPerMeterSingle;
    this.plantsPerMeterText.dataset.plantsPerMeterDouble = this.plantsPerMeterDouble;

    // Get the correct value based on current row type
    const plantsPerMeter = this.currentRowType === 'double' ? this.plantsPerMeterDouble : this.plantsPerMeterSingle;

    if (plantsPerMeter) {
      this.plantsPerMeterText.textContent = `(${plantsPerMeter} ${this.translations.plantsPerMetre || 'plants per metre'})`;
    }
  }

  updateRowTypeVisibility() {
    // Optional UI rule: Hide single row button if bare-root (if root_type is set)
    if (this.rootType !== 'bare-root') return;
    this.rowButtons.forEach((button) => {
      if (button.dataset.rowType === 'single') {
        button.style.display = 'none';
      }
    });

    // Force double row if bare-root
    if (this.currentRowType === 'single') {
      this.setRowType('double');
    }
  }

  setupMetresInput() {
    if (!this.metresInput) return;

    // Restore preserved value if it exists, otherwise set to 0
    const preservedValue = this.container.dataset.preservedMetres;
    if (preservedValue) {
      this.metresInput.value = preservedValue;
    } else if (!this.metresInput.value || this.metresInput.value === '') {
      this.metresInput.value = '0';
      this.container.dataset.preservedMetres = '0';
    }

    // Validation override is already set up in init() before this method is called

    this.metresInput.addEventListener('input', () => {
      if (this.isUpdating) return;
      // Always update preserved value when user changes it
      this.container.dataset.preservedMetres = this.metresInput.value;

      // Update button state after a small delay to ensure value is set
      setTimeout(() => {
        this.updateMetresButtonState();
      }, 0);

      const metres = parseFloat(this.metresInput.value) || 0;
      if (metres > 0) {
        this.calculateAndUpdateQuantity();
      } else {
        this.resetQuantityToMinimum();
      }
    });

    this.metresInput.addEventListener('change', () => {
      if (this.isUpdating) return;
      // Always update preserved value when user changes it
      this.container.dataset.preservedMetres = this.metresInput.value;

      // Update button state after a small delay to ensure value is set
      setTimeout(() => {
        this.updateMetresButtonState();
      }, 0);

      const metres = parseFloat(this.metresInput.value) || 0;
      if (metres > 0) {
        this.calculateAndUpdateQuantity();
      } else {
        this.resetQuantityToMinimum();
      }
    });

    // Initial button state update
    this.updateMetresButtonState();
  }

  overrideMetresInputValidation() {
    // Override the quantity-input's validateQtyRules for the metres input
    // The default validation uses parseInt which doesn't work for decimals (0.1 becomes 0)
    // We need to use parseFloat and allow going to exactly 0
    const quantityInputElement = this.metresInput.closest('quantity-input');
    if (!quantityInputElement) return;

    // Override validateQtyRules for this specific quantity-input
    // Store reference to this for use in the closure
    const metresInput = this.metresInput;
    quantityInputElement.validateQtyRules = () => {
      // Use parseFloat to handle decimal values correctly
      const value = parseFloat(metresInput.value) || 0;
      const min = parseFloat(metresInput.min) || 0;
      const max = parseFloat(metresInput.max) || Infinity;

      const buttonMinus = quantityInputElement.querySelector('.quantity__button[name="minus"]');
      const buttonPlus = quantityInputElement.querySelector('.quantity__button[name="plus"]');

      // For metres input: disable minus only if value is less than min
      // Since min is 0, this allows going down to exactly 0
      // Round to handle floating point precision issues
      const roundedValue = Math.round(value * 10) / 10;
      const roundedMin = Math.round(min * 10) / 10;

      if (buttonMinus) {
        // Disable only if value is less than min (not <=)
        // This allows 0, 0.1, 0.2, etc. but disables if somehow value becomes negative
        const shouldDisable = roundedValue < roundedMin;
        if (shouldDisable) {
          buttonMinus.classList.add('disabled');
        } else {
          buttonMinus.classList.remove('disabled');
        }
      }

      // Disable plus if value is >= max
      if (buttonPlus && max !== Infinity) {
        const shouldDisablePlus = roundedValue >= max;
        if (shouldDisablePlus) {
          buttonPlus.classList.add('disabled');
        } else {
          buttonPlus.classList.remove('disabled');
        }
      }
    };
  }

  updateMetresButtonState() {
    // Manually trigger validation for metres input buttons
    const quantityInputElement = this.metresInput?.closest('quantity-input');
    if (quantityInputElement && typeof quantityInputElement.validateQtyRules === 'function') {
      quantityInputElement.validateQtyRules();
    }
  }

  resetQuantityToMinimum() {
    // Re-find quantity input in case it was recreated
    this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
    if (!this.quantityInput) return;

    // Read min from the input's min attribute (set by setQuantityBoundries) or dataset
    // The min attribute is the actual enforced minimum after all calculations
    const min = parseInt(this.quantityInput.min, 10) || parseInt(this.quantityInput.dataset.min, 10) || 1;
    this.quantityInput.value = min;
  }

  setupQuantityInput() {
    // Re-find quantity input in case it was recreated
    this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
    if (!this.quantityInput) return;

    // Remove old listeners if they exist (to prevent duplicates)
    if (this.quantityInput._hedgeCalculatorInputHandler) {
      this.quantityInput.removeEventListener('input', this.quantityInput._hedgeCalculatorInputHandler);
      this.quantityInput.removeEventListener('change', this.quantityInput._hedgeCalculatorChangeHandler);
    }

    // Create bound handlers
    this.quantityInput._hedgeCalculatorInputHandler = () => {
      if (!this.isUpdating) {
        this.calculateMetresFromQuantity();
      }
    };

    this.quantityInput._hedgeCalculatorChangeHandler = () => {
      if (!this.isUpdating) {
        this.calculateMetresFromQuantity();
      }
    };

    // Listen to quantity changes for reverse calculation (dual control)
    this.quantityInput.addEventListener('input', this.quantityInput._hedgeCalculatorInputHandler);
    this.quantityInput.addEventListener('change', this.quantityInput._hedgeCalculatorChangeHandler);
  }

  calculateAndUpdateQuantity() {
    if (!this.metresInput) return;

    // Re-find quantity input in case it was recreated
    this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
    if (!this.quantityInput) {
      return;
    }

    if (this.quantityInput.disabled) {
      this.resetQuantityToMinimum();
      return;
    }

    const metres = parseFloat(this.metresInput.value) || 0;
    if (metres <= 0) {
      // If 0 or invalid, reset to minimum and hide warning
      this.resetQuantityToMinimum();
      return;
    }

    // Calculate plants using utility function: plantsPerMeter * metres
    if (typeof calculatePlants === 'undefined') {
      return;
    }
    // eslint-disable-next-line no-undef
    const calculatedPlants = calculatePlants(
      metres,
      this.currentRowType,
      this.plantsPerMeterSingle,
      this.plantsPerMeterDouble
    );

    if (calculatedPlants > 0) {
      this.isUpdating = true;

      // Read min from the min attribute (set by setQuantityBoundries) which is the actual enforced minimum
      // Fall back to dataset.min if min attribute is not set
      const min = parseInt(this.quantityInput.min, 10) || parseInt(this.quantityInput.dataset.min, 10) || 1;
      const max = parseInt(this.quantityInput.max, 10) || Infinity;

      // Set quantity within min/max bounds
      const qtyValue = Math.max(min, Math.min(max, calculatedPlants));
      const currentQty = parseInt(this.quantityInput.value, 10) || 0;

      if (currentQty !== qtyValue) {
        this.quantityInput.value = qtyValue;
        // Trigger input event so quantity-input custom element validates
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));

        // Publish quantity update event (but don't trigger our own listener)
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
      }

      this.isUpdating = false;
    }
  }

  calculateMetresFromQuantity() {
    // Re-find quantity input in case it was recreated
    this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
    if (!this.quantityInput || !this.metresInput) return;

    const plants = parseInt(this.quantityInput.value, 10);
    if (!plants || plants <= 0) {
      return;
    }

    // Calculate metres using reverse utility function: plants / plantsPerMeter
    if (typeof calculateMetres === 'undefined') {
      return;
    }
    // eslint-disable-next-line no-undef
    const metres = calculateMetres(plants, this.currentRowType, this.plantsPerMeterSingle, this.plantsPerMeterDouble);

    if (metres > 0) {
      this.isUpdating = true;

      const currentMetres = parseFloat(this.metresInput.value) || 0;
      // Round to 1 decimal
      const roundedMetres = Math.round(metres * 10) / 10;

      if (Math.abs(roundedMetres - currentMetres) > 0.01) {
        this.metresInput.value = roundedMetres;
        // Don't dispatch change event to avoid triggering forward calculation
      }

      this.isUpdating = false;
    }
  }

  subscribeToVariantChanges() {
    // Subscribe to variant changes to update metafield data
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
      return;
    }

    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));

    // Also listen for quantity updates (when product-info updates quantity rules)
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
      // Re-find quantity input and re-setup listeners after quantity rules update
      setTimeout(() => {
        this.setupQuantityInput();
        // If we have metres value, recalculate to update quantity
        // But only if metres > 0 (if 0, let setQuantityBoundries handle it)
        const metres = parseFloat(this.metresInput?.value) || 0;
        if (metres > 0) {
          this.calculateAndUpdateQuantity();
        }
        // If metres is 0, the quantity should already be set to min by setQuantityBoundries
      }, 50);
    });
  }

  handleVariantChange() {
    // Read from dataset first (which should be up-to-date from change events)
    // Fall back to input value if dataset doesn't exist yet
    const preservedMetres = this.container.dataset.preservedMetres || this.metresInput?.value || '0';
    // Ensure it's stored in dataset
    this.container.dataset.preservedMetres = preservedMetres;

    // Restore immediately to prevent flash (don't wait for timeout)
    if (this.metresInput && preservedMetres !== '0') {
      this.metresInput.value = preservedMetres;
    }

    // Wait for DOM updates to complete
    setTimeout(() => {
      // Re-find elements
      this.metresInput = this.container.querySelector('.js-hedge-calculator-inline-metres');
      this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
      this.plantsPerMeterText = this.container.querySelector('.hedge-calculator-inline__no-plants-text');

      // Restore preserved value
      if (this.metresInput && this.container.dataset.preservedMetres) {
        this.metresInput.value = this.container.dataset.preservedMetres;
      }

      // Re-setup metres input validation override (in case element was recreated)
      this.overrideMetresInputValidation();
      this.updateMetresButtonState();

      // Re-setup quantity input listeners
      this.setupQuantityInput();
      this.updateMetafieldData();
      this.updateRowTypeVisibility();
      this.updatePlantsPerMeterText();

      // Recalculate if needed
      // Wait a bit longer to ensure quantity rules have been updated by product-info.js
      setTimeout(() => {
        const metres = parseFloat(this.container.dataset.preservedMetres) || 0;
        if (metres > 0) {
          this.calculateAndUpdateQuantity();
        } else {
          // Reset to minimum - this will use the updated min from the new variant
          this.resetQuantityToMinimum();
        }
      }, 150);
    }, 100);
  }

  updateMetafieldData() {
    // Re-read metafield data from container attributes
    this.variantId = this.container.dataset.variantId;
    this.plantsPerMeterSingle = parseFloat(this.container.dataset.plantsPerMeterSingle) || 0;
    this.plantsPerMeterDouble = parseFloat(this.container.dataset.plantsPerMeterDouble) || 0;
    // Optional: root_type only for UI rules
    this.rootType = this.container.dataset.rootType || null;

    // Validate again
    if (!this.validateMetafields()) {
      // Missing metafields after variant change
    }
  }

  destroy() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }
}
// Initialize all calculators on the page
function initHedgeCalculatorsInline() {
  const calculators = document.querySelectorAll('.js-hedge-calculator-inline');

  calculators.forEach((container) => {
    // Check if already initialized
    if (container.hedgeCalculatorInline) {
      return;
    }

    container.hedgeCalculatorInline = new HedgeCalculatorInline(container);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHedgeCalculatorsInline);
} else {
  initHedgeCalculatorsInline();
}

// Re-initialize after dynamic HTML updates (for variant changes)
document.addEventListener('shopify:section:load', initHedgeCalculatorsInline);

// Re-initialize when product-info is updated (variant changes)
document.addEventListener('product-info:loaded', () => {
  // Small delay to ensure DOM is fully updated
  // Don't re-initialize if calculators already exist - they handle variant changes themselves
  setTimeout(() => {
    const calculators = document.querySelectorAll('.js-hedge-calculator-inline');
    calculators.forEach((container) => {
      // Only initialize if not already initialized
      if (!container.hedgeCalculatorInline) {
        container.hedgeCalculatorInline = new HedgeCalculatorInline(container);
      }
    });
  }, 100);
});

// Also listen for variant changes directly to update existing instances
// Note: Individual calculator instances handle variant changes via handleVariantChange
// This is just for ensuring new calculators are initialized if needed
if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, () => {
    // Only initialize new calculators, don't re-initialize existing ones
    setTimeout(() => {
      const calculators = document.querySelectorAll('.js-hedge-calculator-inline');
      calculators.forEach((container) => {
        if (!container.hedgeCalculatorInline) {
          container.hedgeCalculatorInline = new HedgeCalculatorInline(container);
        }
      });
    }, 100);
  });
}
